import React from 'react';
import { FileText } from 'lucide-react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Resource, UIComponent } from '../../types';

interface RichTextComponentProps {
  resource: Resource;
  component: UIComponent;
  onAction?: (action: string, data?: any) => void;
}

export const RichTextComponent: React.FC<RichTextComponentProps> = ({ resource, component, onAction }) => {
  const { t } = useTranslation();
  
  // Get content from resource
  const content = resource.content || resource.html || resource.text || '';
  const title = resource.title || resource.caption || '';
  const backgroundColor = resource.backgroundColor || resource.bgColor || 'white';
  const textColor = resource.textColor || resource.color || 'inherit';
  const padding = resource.padding || resource.p || '1rem';
  const fontSize = resource.fontSize || resource.font_size || 'inherit';
  const alignment = resource.alignment || resource.align || 'left'; // 'left', 'center', 'right', 'justify'
  const maxWidth = resource.maxWidth || resource.max_width || 'none';

  // Sanitize HTML content (basic sanitization)
  const sanitizeHTML = (html: string): string => {
    // Basic sanitization - in production, use a library like DOMPurify
    // For now, we'll trust the content since it's from the teacher
    return html;
  };

  const getAlignmentClass = () => {
    switch (alignment) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      case 'justify':
        return 'text-justify';
      case 'left':
      default:
        return 'text-left';
    }
  };

  const getContainerStyle = (): React.CSSProperties => {
    const style: React.CSSProperties = {
      backgroundColor,
      color: textColor,
      padding,
    };

    if (fontSize !== 'inherit') {
      style.fontSize = typeof fontSize === 'number' ? `${fontSize}px` : fontSize;
    }

    if (maxWidth !== 'none') {
      style.maxWidth = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;
      style.margin = '0 auto';
    }

    return style;
  };

  if (!content || content.trim() === '') {
    return (
      <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 font-medium">{t('richText.noContent')}</p>
        <p className="text-sm text-gray-500 mt-2">{t('richText.provideContent')}</p>
      </div>
    );
  }

  const sanitizedContent = sanitizeHTML(content);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {title && (
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
      )}
      
      <div
        className={`rich-text-content ${getAlignmentClass()}`}
        style={getContainerStyle()}
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      />
    </div>
  );
};

